// Globals
import { useTranslation } from 'react-i18next';

// Store
import { useAppState } from '@/store';
import { selectPropertyOwner, selectStepsProgresses } from '../../feature';

// Models
import { PhotoshootingStepProgress } from '@/models/propertyProgress.model';
import { StepType } from '@/models/step.model';

// Components
import { Image, ImageGroup } from '@/ui';
import { ResetStepButton, StepCard, StyledLabel, StyledRow, StyledText } from '../../components';
import { StyledList, StyledListTitle } from './PhotoshootingProgressContainerStyles';

// Helpers
import { finalStatuses, getStepUploader } from '../../helpers';

type TPhotoshootingProgressContainerProps = {
  hideResetButton?: boolean;
};

const PhotoshootingProgressContainer = ({ hideResetButton = false }: TPhotoshootingProgressContainerProps) => {
  const { t } = useTranslation('property');

  const stepsProgresses = useAppState(selectStepsProgresses);
  const propertyOwner = useAppState(selectPropertyOwner);

  const photoshootingProgress = stepsProgresses?.[StepType.PHOTOSHOOTING] as PhotoshootingStepProgress;

  if (!photoshootingProgress) return null;

  const user = getStepUploader(photoshootingProgress);

  return (
    <StepCard title={t('step_photoshooting')} status={photoshootingProgress.status}>
      <StyledRow>
        <div>
          {user && (
            <>
              <StyledLabel>{t('label_who_uploaded')}</StyledLabel>
              <StyledText>
                {t('property_upload_by', {
                  context: user,
                  name: propertyOwner?.full_name,
                })}
              </StyledText>
            </>
          )}
        </div>
        {!hideResetButton && finalStatuses.includes(photoshootingProgress.status) && (
          <ResetStepButton propertyId={photoshootingProgress?.property_id} stepName={StepType.PHOTOSHOOTING} />
        )}
      </StyledRow>
      {(!!photoshootingProgress?.photos_inside?.length || !!photoshootingProgress?.photos_outside?.length) && (
        <>
          <div>
            <StyledListTitle>
              {t('photoshooting_inside', {
                count: photoshootingProgress.photos_inside?.length ?? 0,
              })}
            </StyledListTitle>
            <StyledList>
              <ImageGroup
                preview={{
                  toolbarRender: () => null,
                }}
              >
                {photoshootingProgress.photos_inside?.map((file) => (
                  <Image key={file.PK} src={file.file_link} width="12rem" height="12rem" />
                ))}
              </ImageGroup>
            </StyledList>
          </div>

          <div>
            <StyledListTitle>
              {t('photoshooting_outside', {
                count: photoshootingProgress.photos_outside?.length ?? 0,
              })}
            </StyledListTitle>
            <StyledList>
              <ImageGroup
                preview={{
                  toolbarRender: () => null,
                }}
              >
                {photoshootingProgress.photos_outside?.map((file) => (
                  <Image key={file.PK} src={file.file_link} width="12rem" height="12rem" />
                ))}
              </ImageGroup>
            </StyledList>
          </div>
        </>
      )}
    </StepCard>
  );
};

export default PhotoshootingProgressContainer;
